import React, { useMemo, useRef, useState } from 'react';
import {
  MAP_W,
  MAP_H,
  SITE_DEFAULTS,
  ZONES,
  DEVICE_ZONES,
  defaultPosition,
} from '../data/deviceMap';
import { saveDevicePosition, saveZoneLayout, saveSiteLayout } from '../data/firestore';

// Location-accurate device map traced from the satellite photo.
// Modes:
//   control — click a dot to toggle its switch (default)
//   devices — drag dots to their real spots (saved to controlLayout/{id})
//   rooms   — drag room rectangles to move, drag the corner handle to
//             resize (saved to controlLayout/_zones). Toggle the satellite
//             underlay to trace the real footprint.
//   site    — drag corners of the property lines / hillface / retaining
//             wall; + markers insert corners, double-click removes one
//             (saved to controlLayout/_site)
//   measure — click two points and enter the real-world distance (from
//             Google Earth). References set the map scale: a scale bar
//             appears and rooms report their size in feet.

const MIN_ZONE = 30;

// Editable site shapes; 'wall' is an open polyline, the rest are closed.
const SITE_SHAPES = ['lot', 'hill', 'wall', 'structure'];
const SITE_SHAPE_LABELS = {
  lot: 'Lot',
  hill: 'Hillside',
  wall: 'Wall',
  structure: 'House footprint',
};

// Independently toggleable map layers (persisted per browser).
const LAYER_DEFS = [
  ['photo', 'Satellite photo'],
  ['streets', 'Streets'],
  ['lot', 'Property boundary'],
  ['hill', 'Hillside'],
  ['wall', 'Retaining wall'],
  ['structure', 'House footprint'],
  ['rooms', 'Rooms & areas'],
  ['devices', 'Devices'],
];
const DEFAULT_LAYERS = {
  photo: false,
  streets: true,
  lot: true,
  hill: true,
  wall: true,
  structure: true,
  rooms: true,
  devices: true,
};
const LAYERS_KEY = 'ah-map-layers';

const toPath = (pts, close) =>
  pts.map((p, i) => `${i ? 'L' : 'M'} ${p.x} ${p.y}`).join(' ') + (close ? ' Z' : '');

export default function HouseMap({ devices, layout, onToggle, pendingMap }) {
  const [mode, setMode] = useState('control'); // control | devices | rooms | site | measure
  const [layers, setLayers] = useState(() => {
    try {
      return { ...DEFAULT_LAYERS, ...JSON.parse(localStorage.getItem(LAYERS_KEY) || '{}') };
    } catch {
      return { ...DEFAULT_LAYERS };
    }
  });
  const [showLayers, setShowLayers] = useState(false);
  const [placing, setPlacing] = useState(null); // docId picked from the tray
  const [drag, setDrag] = useState(null); // device drag: { id, x, y }
  // zone drag: { id, kind: 'move'|'resize', dx, dy, rect: {x,y,w,h} }
  const [zoneDrag, setZoneDrag] = useState(null);
  // site vertex drag: { shape: 'lot'|'hill'|'wall', idx }
  const [siteDrag, setSiteDrag] = useState(null);
  const [siteShape, setSiteShape] = useState('lot'); // which shape is being edited
  const [siteDraft, setSiteDraft] = useState(null); // in-flight {lot,hill,wall}
  const [measureStart, setMeasureStart] = useState(null); // first clicked point
  const svgRef = useRef(null);

  const setLayer = (id, on) =>
    setLayers((prev) => {
      const next = { ...prev, [id]: on };
      try {
        localStorage.setItem(LAYERS_KEY, JSON.stringify(next));
      } catch {
        /* private mode etc. — layer prefs just won't persist */
      }
      return next;
    });

  const layoutById = useMemo(() => {
    const m = {};
    (layout || []).forEach((d) => {
      m[d.id] = d;
    });
    return m;
  }, [layout]);

  // Effective room rects: code defaults + owner-created custom rooms,
  // overridden by the owner's saved layout, overridden by an in-flight drag.
  const zdoc = layoutById._zones || {};
  const zoneOverrides = zdoc.zones || {};
  const customZones = zdoc.custom || {};
  const zones = useMemo(() => {
    const base = ZONES.map((z) => ({ ...z, ...(zoneOverrides[z.id] || {}) }));
    const extras = Object.entries(customZones).map(([id, c]) => ({
      id,
      type: c.type || 'room',
      ...c,
      custom: true,
    }));
    return [...base, ...extras].map((z) =>
      zoneDrag && zoneDrag.id === z.id ? { ...z, ...zoneDrag.rect } : z
    );
  }, [zoneOverrides, customZones, zoneDrag]);
  const zoneById = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);

  // Persist the full zone set (built-in overrides + custom rooms).
  const persistZones = (zonesList) => {
    const builtin = {};
    const custom = {};
    zonesList.forEach((z) => {
      const rect = {
        x: Math.round(z.x),
        y: Math.round(z.y),
        w: Math.round(z.w),
        h: Math.round(z.h),
        label: z.label,
      };
      if (z.custom) custom[z.id] = { ...rect, type: z.type };
      else builtin[z.id] = rect;
    });
    saveZoneLayout(builtin, custom);
  };

  const addRoom = () => {
    const name = window.prompt('Name for the new room / area?');
    if (!name || !name.trim()) return;
    const outdoor = window.confirm('Outdoor area? OK = outdoor, Cancel = interior room');
    const id = `c-${name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${Date.now().toString(36)}`;
    persistZones(
      zones.concat([
        { id, label: name.trim(), type: outdoor ? 'outdoor' : 'room', x: 440, y: 340, w: 110, h: 80, custom: true },
      ])
    );
  };

  const renameZone = (zone) => {
    const name = window.prompt('Rename this area:', zone.label);
    if (!name || !name.trim() || name.trim() === zone.label) return;
    persistZones(zones.map((z) => (z.id === zone.id ? { ...z, label: name.trim() } : z)));
  };

  const deleteZone = (zone) => {
    if (!zone.custom) return;
    if (!window.confirm(`Delete "${zone.label}"?`)) return;
    persistZones(zones.filter((z) => z.id !== zone.id));
  };

  // ---- site geometry (property lines, hill, wall) + measured distances ----
  const savedSite = layoutById._site || {};
  const site =
    siteDraft ||
    Object.fromEntries(
      SITE_SHAPES.map((s) => [s, savedSite[s] || SITE_DEFAULTS[s]])
    );
  const refs = savedSite.refs || [];

  const persistSite = (nextSite, nextRefs = refs) => {
    const rounded = Object.fromEntries(
      SITE_SHAPES.map((s) => [
        s,
        nextSite[s].map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) })),
      ])
    );
    saveSiteLayout({ ...rounded, refs: nextRefs });
  };

  // Map scale from the owner's Google-Earth reference distances (average of
  // ft-per-viewBox-unit across all of them).
  const ftPerUnit = useMemo(() => {
    const usable = refs.filter((r) => r.ft > 0);
    if (!usable.length) return null;
    const sum = usable.reduce(
      (acc, r) => acc + r.ft / Math.max(1, Math.hypot(r.x2 - r.x1, r.y2 - r.y1)),
      0
    );
    return sum / usable.length;
  }, [refs]);
  const fmtFt = (units) => (ftPerUnit ? `${Math.round(units * ftPerUnit)} ft` : null);

  const startVertexDrag = (shape, idx) => (evt) => {
    if (mode !== 'site') return;
    evt.preventDefault();
    evt.stopPropagation();
    evt.target.setPointerCapture?.(evt.pointerId);
    setSiteDraft({ ...site });
    setSiteDrag({ shape, idx });
  };

  // Click anywhere along an edge to insert a corner at that exact spot
  // (clamped just off the endpoints so it can't stack on an existing corner).
  const insertVertexAt = (shape, idx) => (evt) => {
    evt.stopPropagation();
    const p = toMapCoords(evt);
    const pts = [...site[shape]];
    const a = pts[idx];
    const b = pts[(idx + 1) % pts.length];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const t = Math.max(
      0.04,
      Math.min(0.96, ((p.x - a.x) * abx + (p.y - a.y) * aby) / (abx * abx + aby * aby || 1))
    );
    pts.splice(idx + 1, 0, { x: Math.round(a.x + t * abx), y: Math.round(a.y + t * aby) });
    persistSite({ ...site, [shape]: pts });
  };

  // Set an edge's true length: the SECOND corner (clockwise) slides along
  // the edge direction so the drawn length matches the known distance.
  const setEdgeLength = (shape, idx) => (evt) => {
    evt.stopPropagation();
    if (!ftPerUnit) return;
    const pts = [...site[shape]];
    const a = pts[idx];
    const bIdx = (idx + 1) % pts.length;
    const b = pts[bIdx];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const input = window.prompt(
      'True edge length in feet (the far corner slides to match):',
      (len * ftPerUnit).toFixed(1)
    );
    if (input === null) return;
    const ft = parseFloat(input);
    if (!ft || ft <= 0) return;
    const k = ft / ftPerUnit / (len || 1);
    pts[bIdx] = { x: Math.round(a.x + (b.x - a.x) * k), y: Math.round(a.y + (b.y - a.y) * k) };
    persistSite({ ...site, [shape]: pts });
  };

  const removeVertex = (shape, idx) => {
    const minPts = shape === 'wall' ? 2 : 3;
    const pts = site[shape];
    if (pts.length <= minPts) return;
    persistSite({ ...site, [shape]: pts.filter((_, i) => i !== idx) });
  };

  // Type a room's true size in feet (needs the scale from measured refs);
  // resizes the rect in place keeping its top-left corner.
  const setZoneSize = (zone) => {
    if (!ftPerUnit) return;
    const input = window.prompt(
      `Real size of "${zone.label}" in feet — width x height:`,
      `${(zone.w * ftPerUnit).toFixed(1)} x ${(zone.h * ftPerUnit).toFixed(1)}`
    );
    if (!input) return;
    const m = input.match(/([\d.]+)\s*[x×by,]+\s*([\d.]+)/i);
    if (!m) return;
    const w = parseFloat(m[1]) / ftPerUnit;
    const h = parseFloat(m[2]) / ftPerUnit;
    if (!(w > 0) || !(h > 0)) return;
    persistZones(zones.map((z) => (z.id === zone.id ? { ...z, w, h } : z)));
  };

  const editRef = (index) => {
    const r = refs[index];
    const input = window.prompt('Distance in feet (leave empty to delete):', r.ft);
    if (input === null) return;
    const ft = parseFloat(input);
    const next = [...refs];
    if (!ft || ft <= 0) next.splice(index, 1);
    else next[index] = { ...r, ft };
    persistSite(site, next);
  };

  // Only physical devices go on the map; scenes live in the Devices tab.
  const mappable = useMemo(
    () => (devices || []).filter((d) => d.kind === 'device'),
    [devices]
  );

  const { placed, unplaced } = useMemo(() => {
    const zoneMembers = {};
    mappable.forEach((d) => {
      const z = DEVICE_ZONES[d.id];
      if (z) (zoneMembers[z] = zoneMembers[z] || []).push(d.id);
    });
    Object.values(zoneMembers).forEach((ids) => ids.sort());
    const placedList = [];
    const unplacedList = [];
    mappable.forEach((d) => {
      const saved = layoutById[d.id];
      if (saved && typeof saved.x === 'number') {
        placedList.push({ device: d, x: saved.x, y: saved.y });
        return;
      }
      const zoneId = DEVICE_ZONES[d.id];
      if (zoneId && zoneById[zoneId]) {
        const ids = zoneMembers[zoneId];
        const pos = defaultPosition(zoneById[zoneId], ids.indexOf(d.id), ids.length);
        placedList.push({ device: d, ...pos });
      } else {
        unplacedList.push(d);
      }
    });
    return { placed: placedList, unplaced: unplacedList };
  }, [mappable, layoutById, zoneById]);

  const toMapCoords = (evt) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(MAP_W, ((evt.clientX - rect.left) / rect.width) * MAP_W)),
      y: Math.max(0, Math.min(MAP_H, ((evt.clientY - rect.top) / rect.height) * MAP_H)),
    };
  };

  // Snap measurement endpoints to the nearest lot/hill/wall vertex or room
  // corner (within 15 viewBox units), so spans measured along a border are
  // anchored to the exact same points the border is drawn with.
  const snapPoint = (p) => {
    let best = null;
    let bestD = 15;
    const consider = (x, y) => {
      const d = Math.hypot(x - p.x, y - p.y);
      if (d < bestD) {
        bestD = d;
        best = { x, y };
      }
    };
    // Only snap to visible layers — hiding a layer removes its corners
    // from consideration, which helps when shapes overlap.
    SITE_SHAPES.forEach((s) => {
      if (layers[s]) site[s].forEach((pt) => consider(pt.x, pt.y));
    });
    if (layers.rooms) {
      zones.forEach((z) => {
        consider(z.x, z.y);
        consider(z.x + z.w, z.y);
        consider(z.x, z.y + z.h);
        consider(z.x + z.w, z.y + z.h);
      });
    }
    return best || p;
  };

  // ---- device tray placement + measurement clicks ----
  const onMapClick = (evt) => {
    if (mode === 'measure') {
      const p = snapPoint(toMapCoords(evt));
      if (!measureStart) {
        setMeasureStart(p);
        return;
      }
      const start = measureStart;
      setMeasureStart(null);
      const input = window.prompt('Real distance in feet (from Google Earth):');
      const ft = parseFloat(input);
      if (!ft || ft <= 0) return;
      persistSite(
        site,
        refs.concat([
          {
            x1: Math.round(start.x),
            y1: Math.round(start.y),
            x2: Math.round(p.x),
            y2: Math.round(p.y),
            ft,
          },
        ])
      );
      return;
    }
    if (mode !== 'devices' || !placing) return;
    const { x, y } = toMapCoords(evt);
    saveDevicePosition(placing, x, y);
    setPlacing(null);
  };

  const onDotPointerDown = (device) => (evt) => {
    if (mode !== 'devices') return;
    evt.preventDefault();
    evt.target.setPointerCapture?.(evt.pointerId);
    const { x, y } = toMapCoords(evt);
    setDrag({ id: device.id, x, y });
  };

  // ---- room dragging / resizing ----
  const startZoneDrag = (zone, kind) => (evt) => {
    if (mode !== 'rooms') return;
    evt.preventDefault();
    evt.stopPropagation();
    evt.target.setPointerCapture?.(evt.pointerId);
    const p = toMapCoords(evt);
    setZoneDrag({
      id: zone.id,
      kind,
      dx: p.x - zone.x,
      dy: p.y - zone.y,
      rect: { x: zone.x, y: zone.y, w: zone.w, h: zone.h },
    });
  };

  const onPointerMove = (evt) => {
    if (drag) {
      setDrag({ ...drag, ...toMapCoords(evt) });
      return;
    }
    if (zoneDrag) {
      const p = toMapCoords(evt);
      const r = { ...zoneDrag.rect };
      if (zoneDrag.kind === 'move') {
        r.x = Math.max(0, Math.min(MAP_W - r.w, p.x - zoneDrag.dx));
        r.y = Math.max(0, Math.min(MAP_H - r.h, p.y - zoneDrag.dy));
      } else {
        r.w = Math.max(MIN_ZONE, Math.min(MAP_W - r.x, p.x - r.x));
        r.h = Math.max(MIN_ZONE, Math.min(MAP_H - r.y, p.y - r.y));
      }
      setZoneDrag({ ...zoneDrag, rect: r });
      return;
    }
    if (siteDrag && siteDraft) {
      const p = toMapCoords(evt);
      const pts = siteDraft[siteDrag.shape].map((pt, i) =>
        i === siteDrag.idx ? { x: p.x, y: p.y } : pt
      );
      setSiteDraft({ ...siteDraft, [siteDrag.shape]: pts });
    }
  };

  const onPointerUp = () => {
    if (drag) {
      saveDevicePosition(drag.id, drag.x, drag.y);
      setDrag(null);
    }
    if (zoneDrag) {
      // `zones` already carries the in-flight rect for the dragged zone.
      persistZones(zones);
      setZoneDrag(null);
    }
    if (siteDrag) {
      if (siteDraft) persistSite(siteDraft);
      setSiteDrag(null);
      setSiteDraft(null);
    }
  };

  const hints = {
    control: 'Click a dot to toggle it. Amber = on.',
    devices: placing
      ? 'Click the map to place the selected device.'
      : 'Drag dots to their real locations — saved automatically.',
    rooms:
      'Drag to move, resize by the corner square, double-click to rename — or click a room\u2019s ft label to type its exact size.',
    site: `Editing the ${SITE_SHAPE_LABELS[siteShape].toLowerCase()}: drag corners, click anywhere on an edge to add one, double-click a corner to remove it${ftPerUnit ? ', click an edge length to type the true distance' : ''}.`,
    measure: measureStart
      ? 'Click the second point of the measured span.'
      : 'Click two points, then enter the real distance from Google Earth. Click a label to edit or delete it.',
  };

  return (
    <div className="ah-map-wrap">
      <div className="ah-map-toolbar">
        <span className="ah-map-hint">{hints[mode]}</span>
        <div className="ah-map-modes">
          {mode === 'rooms' && (
            <button type="button" className="cs-btn cs-btn-sm" onClick={addRoom}>
              + Add room
            </button>
          )}
          {mode === 'site' && (
            <span className="ah-map-shape-picker">
              {SITE_SHAPES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`cs-btn cs-btn-sm ${siteShape === s ? '' : 'cs-btn-outline'}`}
                  onClick={() => {
                    setSiteShape(s);
                    setLayer(s, true);
                  }}
                >
                  <span className={`ah-map-shape-swatch ${s}`} />
                  {SITE_SHAPE_LABELS[s]}
                </button>
              ))}
            </span>
          )}
          <span className="ah-map-layers-anchor">
            <button
              type="button"
              className={`cs-btn cs-btn-sm ${showLayers ? '' : 'cs-btn-outline'}`}
              onClick={() => setShowLayers(!showLayers)}
            >
              Layers
            </button>
            {showLayers && (
              <div className="ah-map-layers-panel">
                {LAYER_DEFS.map(([id, label]) => (
                  <label key={id}>
                    <input
                      type="checkbox"
                      checked={!!layers[id]}
                      onChange={(evt) => setLayer(id, evt.target.checked)}
                    />
                    <span className={`ah-map-shape-swatch ${id}`} />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </span>
          {[
            ['control', 'Control'],
            ['devices', 'Move devices'],
            ['rooms', 'Edit rooms'],
            ['site', 'Lot & hill'],
            ['measure', 'Measure'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`cs-btn cs-btn-sm ${mode === id ? '' : 'cs-btn-outline'}`}
              onClick={() => {
                setMode(id);
                setPlacing(null);
                setMeasureStart(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        className={`ah-map mode-${mode}`}
        onClick={onMapClick}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* satellite underlay — the map was traced from this exact photo, so
            stretching it to the viewBox lines it up with the drawn geometry */}
        {layers.photo && (
          <g transform="matrix(0.79976, -0.15522, 0.20600, 1.06141, 74.39, 73.32)">
            {/* 2026-08 Google Earth capture, aligned by the owner's measured
                168.35 ft flat-land/hill edge (scripts/align-satellite.py). */}
            <image
              href="/property/satellite-2026-08.png"
              x="0"
              y="0"
              width={MAP_W}
              height={MAP_H}
              preserveAspectRatio="none"
              opacity="0.5"
            />
          </g>
        )}

        {/* streets: Amalfi Dr down the east side, Upper Mesa Rd down the
            west — they converge at the lot's southern point. */}
        {layers.streets && (
          <g>
            {/* centerlines offset from the 2026-08 lot edges: Amalfi ~42
                units east of the east boundary, Upper Mesa ~60 units below
                the hill's outer edge, plus the junction band where they
                merge south of the lot */}
            <path d="M 859 0 L 832 820" className="ah-map-street" />
            <path d="M 0 145 L 616 820" className="ah-map-street" />
            <path d="M 609 714 L 842 686" className="ah-map-street" />
            <text x="846" y="350" className="ah-map-street-label" transform="rotate(-88 846 350)">
              Amalfi Dr
            </text>
            <text x="246" y="414" className="ah-map-street-label" transform="rotate(48 246 414)">
              Upper Mesa Rd
            </text>
          </g>
        )}

        {/* the shape being edited in Lot & hill mode always shows, even if
            its layer is hidden */}
        {(layers.hill || (mode === 'site' && siteShape === 'hill')) && (
          <g>
            <path
              d={toPath(site.hill, true)}
              className={`ah-map-hill ${mode === 'site' && siteShape === 'hill' ? 'active' : ''}`}
            />
            <text x="327" y="315" className="ah-map-outside-label" transform="rotate(48 327 315)">
              hillside
            </text>
          </g>
        )}

        {/* lot + retaining wall atop the hillface (west edge) */}
        {(layers.lot || (mode === 'site' && siteShape === 'lot')) && (
          <path
            d={toPath(site.lot, true)}
            className={`ah-map-lot ${mode === 'site' && siteShape === 'lot' ? 'active' : ''}`}
          />
        )}
        {(layers.wall || (mode === 'site' && siteShape === 'wall')) && (
          <path
            d={toPath(site.wall, false)}
            className={`ah-map-wall ${mode === 'site' && siteShape === 'wall' ? 'active' : ''}`}
          />
        )}

        {/* house structure footprint (teal) — rooms line up inside this */}
        {(layers.structure || (mode === 'site' && siteShape === 'structure')) && (
          <path
            d={toPath(site.structure, true)}
            className={`ah-map-structure ${mode === 'site' && siteShape === 'structure' ? 'active' : ''}`}
          />
        )}
        {mode === 'site' && siteShape === 'structure' && (
          <text
            x={site.structure.reduce((a, p) => a + p.x, 0) / site.structure.length}
            y={site.structure.reduce((a, p) => a + p.y, 0) / site.structure.length}
            className="ah-map-structure-label"
          >
            structure
          </text>
        )}

        {/* zones */}
        {(layers.rooms || mode === 'rooms') && zones.map((z) => (
          <g key={z.id}>
            <rect
              x={z.x}
              y={z.y}
              width={z.w}
              height={z.h}
              className={`ah-map-zone ${z.type} ${mode === 'rooms' ? 'editable' : ''}`}
              onPointerDown={startZoneDrag(z, 'move')}
              onDoubleClick={() => mode === 'rooms' && renameZone(z)}
            >
              {ftPerUnit && <title>{`${z.label} — ${fmtFt(z.w)} × ${fmtFt(z.h)}`}</title>}
            </rect>
            {/* the pool's water is drawn from its zone, so it moves/resizes
                with the rectangle like everything else */}
            {z.id === 'pool' && (
              <ellipse
                cx={z.x + z.w / 2}
                cy={z.y + z.h / 2}
                rx={Math.max(10, z.w / 2 - 6)}
                ry={Math.max(8, z.h / 2 - 6)}
                className="ah-map-pool"
              />
            )}
            <text x={z.x + 6} y={z.y + 15} className="ah-map-zone-label">
              {z.label}
            </text>
            {mode === 'rooms' && (
              <rect
                x={z.x + z.w - 12}
                y={z.y + z.h - 12}
                width="12"
                height="12"
                className="ah-map-zone-handle"
                onPointerDown={startZoneDrag(z, 'resize')}
              />
            )}
            {ftPerUnit && mode === 'rooms' && (
              <text
                x={z.x + z.w / 2}
                y={z.y + z.h / 2 + 5}
                className="ah-map-dim-live"
                onClick={(evt) => {
                  evt.stopPropagation();
                  setZoneSize(z);
                }}
                onPointerDown={(evt) => evt.stopPropagation()}
              >
                {`${(z.w * ftPerUnit).toFixed(0)}′×${(z.h * ftPerUnit).toFixed(0)}′`}
              </text>
            )}
            {mode === 'rooms' && z.custom && (
              <g
                className="ah-map-zone-del"
                transform={`translate(${z.x + z.w - 2}, ${z.y + 2})`}
                onClick={(evt) => {
                  evt.stopPropagation();
                  deleteZone(z);
                }}
                onPointerDown={(evt) => evt.stopPropagation()}
              >
                <circle r="8" />
                <text y="3.5">×</text>
              </g>
            )}
          </g>
        ))}

        {/* compass */}
        <g className="ah-map-compass" transform="translate(72, 112)">
          <line x1="0" y1="22" x2="0" y2="-18" />
          <path d="M 0 -26 L -7 -10 L 7 -10 Z" />
          <text x="0" y="42">N</text>
        </g>

        {/* site vertex editing (Lot & hill mode) — one shape at a time,
            picked in the toolbar, so handles never pile up */}
        {mode === 'site' &&
          (() => {
            const shape = siteShape;
            const pts = site[shape];
            const closed = shape !== 'wall';
            const segs = closed ? pts.length : pts.length - 1;
            const cx = pts.reduce((acc, p) => acc + p.x, 0) / pts.length;
            const cy = pts.reduce((acc, p) => acc + p.y, 0) / pts.length;
            return (
              <g key={shape}>
                {/* fat invisible edge lines: click to insert a corner at
                    that exact spot along the edge */}
                {Array.from({ length: segs }).map((_, i) => {
                  const a = pts[i];
                  const b = pts[(i + 1) % pts.length];
                  return (
                    <line
                      key={`edge-${i}`}
                      x1={a.x}
                      y1={a.y}
                      x2={b.x}
                      y2={b.y}
                      className="ah-map-edge-hit"
                      onClick={insertVertexAt(shape, i)}
                      onPointerDown={(evt) => evt.stopPropagation()}
                    />
                  );
                })}
                {/* live edge lengths — click one to type the measured distance */}
                {ftPerUnit &&
                  Array.from({ length: segs }).map((_, i) => {
                    const a = pts[i];
                    const b = pts[(i + 1) % pts.length];
                    const len = Math.hypot(b.x - a.x, b.y - a.y);
                    if (len < 14) return null;
                    const mx = (a.x + b.x) / 2;
                    const my = (a.y + b.y) / 2;
                    // offset the label outward, away from the polygon centroid
                    let ox = my - cy;
                    let oy = -(mx - cx);
                    const ol = Math.hypot(ox, oy) || 1;
                    if ((mx + ox / ol - cx) ** 2 + (my + oy / ol - cy) ** 2 <
                        (mx - cx) ** 2 + (my - cy) ** 2) {
                      ox = -ox;
                      oy = -oy;
                    }
                    return (
                      <text
                        key={`elen-${i}`}
                        x={mx + (ox / ol) * 16}
                        y={my + (oy / ol) * 16 + 4}
                        className={`ah-map-edge-len ${shape}`}
                        onClick={setEdgeLength(shape, i)}
                        onPointerDown={(evt) => evt.stopPropagation()}
                      >
                        {`${(len * ftPerUnit).toFixed(1)}′`}
                      </text>
                    );
                  })}
                {pts.map((p, i) => (
                  <circle
                    key={`v-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r="7"
                    className={`ah-map-vertex ${shape}`}
                    onPointerDown={startVertexDrag(shape, i)}
                    onDoubleClick={(evt) => {
                      evt.stopPropagation();
                      removeVertex(shape, i);
                    }}
                  />
                ))}
              </g>
            );
          })()}

        {/* measured reference distances (Google Earth) — only in Measure
            mode; elsewhere they collide with live edge/room labels */}
        {mode === 'measure' &&
          refs.map((r, i) => {
            const mx = (r.x1 + r.x2) / 2;
            const my = (r.y1 + r.y2) / 2;
            return (
              <g key={`ref-${i}`} className="ah-map-ref">
                <line x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} />
                <circle cx={r.x1} cy={r.y1} r="3" />
                <circle cx={r.x2} cy={r.y2} r="3" />
                <text
                  x={mx}
                  y={my - 6}
                  className="ah-map-ref-label"
                  onClick={(evt) => {
                    evt.stopPropagation();
                    if (mode === 'measure') editRef(i);
                  }}
                >
                  {`${r.ft} ft`}
                </text>
              </g>
            );
          })}

        {/* first click of an in-progress measurement */}
        {measureStart && (
          <g className="ah-map-measure-start" transform={`translate(${measureStart.x}, ${measureStart.y})`}>
            <circle r="5" />
            <line x1="-9" y1="0" x2="9" y2="0" />
            <line x1="0" y1="-9" x2="0" y2="9" />
          </g>
        )}

        {/* scale bar once at least one distance is known */}
        {ftPerUnit && (
          <g className="ah-map-scalebar" transform={`translate(40, ${MAP_H - 34})`}>
            <line x1="0" y1="0" x2={50 / ftPerUnit} y2="0" />
            <line x1="0" y1="-5" x2="0" y2="5" />
            <line x1={50 / ftPerUnit} y1="-5" x2={50 / ftPerUnit} y2="5" />
            <text x={25 / ftPerUnit} y="18">50 ft</text>
          </g>
        )}

        {/* device dots */}
        {(layers.devices || mode === 'devices') && placed.map(({ device, x, y }) => {
          const pos = drag && drag.id === device.id ? drag : { x, y };
          const busy = !!pendingMap[device.address];
          return (
            <g
              key={device.id}
              transform={`translate(${pos.x}, ${pos.y})`}
              className={`ah-map-dot ${device.isOn ? 'on' : ''} ${busy ? 'busy' : ''}`}
              onPointerDown={onDotPointerDown(device)}
              onClick={(evt) => {
                evt.stopPropagation();
                if (mode === 'control') onToggle(device);
              }}
            >
              <title>
                {`${device.name}${device.dimmable ? ` — ${Math.round(((device.level || 0) / 255) * 100)}%` : ''}`}
              </title>
              <circle r="9" className="ah-map-dot-c" />
              {device.dimmable && device.isOn && (
                <circle r="12.5" className="ah-map-dot-ring" />
              )}
            </g>
          );
        })}
      </svg>

      {unplaced.length > 0 && (
        <div className="ah-map-tray">
          <span className="ah-map-tray-label">
            Unplaced ({unplaced.length})
            {mode === 'devices' ? ' — pick one, then click the map' : ''}:
          </span>
          {unplaced.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`ah-map-chip ${d.isOn ? 'on' : ''} ${placing === d.id ? 'picked' : ''}`}
              onClick={() => (mode === 'devices' ? setPlacing(d.id) : onToggle(d))}
              title={d.address}
            >
              {d.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
